/**
 * Economy tuning (IMPLEMENTATION_PLAN.md A2/A5).
 *
 * **The wallet pays for outcomes, not for time.** A tanker brought in safe pays her
 * contract, flat; a supply hull's value is the manifest she carries. Nothing the player
 * earns is a function of how long a shift ran.
 *
 * That is a deliberate reversal. Income used to be the oil in storage at the moment a
 * tanker collected, and storage filled by the minute — so shift *duration* set shift
 * *income*. When the pacing pass cut three to four minutes of dead air out of every
 * shift, it would have cut roughly half the player's money with it, in the same campaign
 * that now throws more hostiles at them. Rather than paying that back with padding, the
 * coupling itself is gone: a tight four-minute shift and a baggy eight-minute one pay the
 * same, because the same ships came through. Every future pacing change is economy-neutral
 * by construction.
 *
 * Production and storage stay, and still mean something — they are the fiction of what
 * the tanker lifts, and the shift report still counts the barrels — but the money follows
 * the delivery.
 *
 * Coherence check: 7 tankers across the eight-shift campaign at $650k is ~$4.55M gross,
 * against ~$2.2M for the entire upgrade tree, so a careful player finishes well equipped
 * with room to have made mistakes. (The fee rose from $450k when the 2026-07-30 cut took
 * the campaign from eleven contracts to seven — the per-contract number moved so the
 * campaign totals would not.) Losing a tanker costs the contract *and* the penalty, which
 * is the sharpest single swing in the game. If any number here moves, the others move.
 */

import type { Resources } from '../sim/types';

export const ECONOMY = {
  /**
   * Paid on a tanker reaching the Safe Line, whatever the shift's length or the state of
   * storage. This is the campaign's income.
   */
  tankerContractFee: 650000,
  /** Dollars per barrel — the shift report's valuation of what was lifted. */
  oilPrice: 80,
  /** Barrels produced per minute at full crew. */
  productionRate: 750,
  /** Storage ceiling; production halts here, so a missed tanker still idles the platform. */
  storageCap: 12000,
  /** Crew level below which production scales down linearly. */
  crewProductionThreshold: 60,
  /** Crew level below which a reload crew is lost. */
  crewReloadThreshold: 30,
  /** Crew level that forces evacuation and ends the campaign. */
  crewEvacuation: 10,
  /** Fraction of a lost supply manifest refunded by insurance. */
  insuranceRate: 0.4,
  /** Flat contract penalty for losing an inbound tanker. */
  tankerLossPenalty: 50000,
  /**
   * Dockside repair cost per integrity point, between shifts.
   *
   * Down from 2,000: the redesigned campaign puts materially more fire on the rig, and
   * repair is maintenance rather than a decision — a player should never be choosing
   * between a working platform and a working gun.
   */
  repairCostPerPoint: 1500,
  /** Seconds a friendly spends inside the ring transferring cargo. */
  transferSeconds: 20,
} as const;

/*
 * A deeper magazine than the game shipped with.
 *
 * The redesigned shifts field about thirty per cent more hostiles, and running dry
 * mid-watch is the one failure that is not the player's fault — the gun is the game, and
 * it should not stop. Torpedoes are the deliberate exception and stay at two: Grey Hull
 * (shift 6) is built entirely on the question of how many are left, and that question
 * has to be able to get a frightening answer.
 */
export const STARTING_RESOURCES: Resources = {
  money: 150000,
  oil: 0,
  oilCapacity: ECONOMY.storageCap,
  integrity: 100,
  crew: 100,
  crewCapacity: 120,
  ammo: 2400,
  ammoReserve: 2400,
  torpedoes: 2,
  depthCharges: 8,
};

export interface ManifestItem {
  id: string;
  label: string;
  cost: number;
  /** What it does on arrival. */
  ammo?: number;
  torpedoes?: number;
  depthCharges?: number;
  repairPercent?: number;
  crew?: number;
}

/*
 * Consumables are cheaper and larger than they were.
 *
 * Restocking is the cost of having played, not a decision worth agonising over — a
 * player who spent a magazine defending a tanker did the right thing and should not be
 * taxed for it. The upgrade tree is where money is supposed to feel scarce; the
 * ammunition counter is not.
 */
export const MANIFEST_ITEMS: readonly ManifestItem[] = [
  { id: 'ammo', label: 'Ammunition crate (900 rds)', cost: 20000, ammo: 900 },
  { id: 'torpedo', label: 'Heavy torpedo', cost: 20000, torpedoes: 1 },
  { id: 'dc', label: 'Depth charges ×4', cost: 6000, depthCharges: 4 },
  { id: 'repair', label: 'Repair kit (+20% integrity)', cost: 30000, repairPercent: 20 },
  { id: 'crew', label: 'Crew team (+12)', cost: 40000, crew: 12 },
];

/*
 * Two ids are gone: 'torpedoSeeker' and 'floodlights'.
 *
 * Both were sold and neither was ever implemented. The seeker promised terminal homing,
 * which the torpedo lock has since made meaningless — the outcome is rolled at launch,
 * so homing either changes nothing or overturns the odds the curve specifies. The
 * floodlight array promised a wider lit radius, which the sim could do easily but the
 * renderer could not match, and a light you see disagreeing with the range you detect
 * at is worse than no upgrade. A save that already contains either simply ignores it.
 */
export type UpgradeId =
  | 'rifledBarrel' | 'autoloader' | 'heAmmo' | 'apAmmo' | 'stabiliser'
  | 'thermal' | 'longRadar' | 'hydrophone'
  | 'torpedoScrews' | 'torpedoTubes'
  | 'projectorRails'
  | 'blastPlating' | 'repairDrones' | 'productionBoost' | 'storageTanks';

export interface Upgrade {
  id: UpgradeId;
  label: string;
  category: 'Autocannon' | 'Sensors' | 'Torpedoes' | 'Depth charges' | 'Rig';
  cost: number;
  description: string;
}

export const UPGRADES: readonly Upgrade[] = [
  { id: 'rifledBarrel', label: 'Rifled barrel', category: 'Autocannon', cost: 120000, description: 'Dispersion −30%' },
  { id: 'autoloader', label: 'Autoloader', category: 'Autocannon', cost: 150000, description: 'Reload twice as fast' },
  { id: 'heAmmo', label: 'HE ammunition', category: 'Autocannon', cost: 180000, description: '+50% vs soft targets' },
  { id: 'apAmmo', label: 'AP ammunition', category: 'Autocannon', cost: 200000, description: 'Armour effect 25% → 60%' },
  /*
   * Was "scope sway −50%", which described a mechanic that does not exist: the sight
   * picture never moves, and weather expresses itself purely as a wider dispersion
   * cone. Now it halves exactly that, which is the real thing the sea does to your aim.
   */
  { id: 'stabiliser', label: 'Gun stabiliser', category: 'Autocannon', cost: 90000, description: 'Weather dispersion −50%' },

  { id: 'thermal', label: 'Thermal optic', category: 'Sensors', cost: 160000, description: 'See through night and fog in scope' },
  { id: 'longRadar', label: 'Long-range radar', category: 'Sensors', cost: 140000, description: 'Contacts at 12 km' },
  { id: 'hydrophone', label: 'Hydrophone array', category: 'Sensors', cost: 170000, description: 'Submerged contacts estimated on radar' },

  { id: 'torpedoScrews', label: 'High-speed screws', category: 'Torpedoes', cost: 130000, description: '+25% torpedo speed' },
  { id: 'torpedoTubes', label: 'Extra tube pair', category: 'Torpedoes', cost: 110000, description: '+2 tubes loaded at once' },

  { id: 'projectorRails', label: 'Extended projector rails', category: 'Depth charges', cost: 90000, description: 'Lob range +300 m' },

  { id: 'blastPlating', label: 'Blast plating', category: 'Rig', cost: 200000, description: 'Rig damage −25%' },
  { id: 'repairDrones', label: 'Repair drones', category: 'Rig', cost: 180000, description: '+2% integrity per minute' },
  { id: 'productionBoost', label: 'Production boost', category: 'Rig', cost: 150000, description: '+150 bbl/min' },
  { id: 'storageTanks', label: 'Storage tanks', category: 'Rig', cost: 100000, description: '+6,000 bbl capacity' },
];

export function upgradeById(id: UpgradeId): Upgrade | undefined {
  return UPGRADES.find((u) => u.id === id);
}
